/* Inline stroke icons — no icon dependency, and they inherit currentColor. */

interface Props {
  size?: number;
  className?: string;
  strokeWidth?: number;
}

function Svg({ size = 16, className, strokeWidth = 1.8, children }: Props & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export const IconOverview = (p: Props) => (
  <Svg {...p}>
    <rect x="3" y="3" width="7" height="9" rx="1.5" />
    <rect x="14" y="3" width="7" height="5" rx="1.5" />
    <rect x="14" y="12" width="7" height="9" rx="1.5" />
    <rect x="3" y="16" width="7" height="5" rx="1.5" />
  </Svg>
);

export const IconPlatforms = (p: Props) => (
  <Svg {...p}>
    <rect x="2" y="4" width="13" height="9" rx="1.5" />
    <path d="M6 17h6" />
    <rect x="17" y="9" width="5" height="11" rx="1.5" />
  </Svg>
);

export const IconEngagement = (p: Props) => (
  <Svg {...p}>
    <path d="M3 3v16a2 2 0 0 0 2 2h16" />
    <path d="M7 15l4-5 3 3 5-7" />
  </Svg>
);

export const IconFeatures = (p: Props) => (
  <Svg {...p}>
    <path d="M12 2l2.6 6.3 6.9.5-5.2 4.4 1.6 6.6L12 16.7 6.1 19.8l1.6-6.6L2.5 8.8l6.9-.5z" />
  </Svg>
);

export const IconStability = (p: Props) => (
  <Svg {...p}>
    <path d="M12 3l8 3.5v5c0 4.8-3.3 8.9-8 10.2-4.7-1.3-8-5.4-8-10.2v-5z" />
    <path d="M12 8.5v4" />
    <path d="M12 16h.01" />
  </Svg>
);

export const IconReleases = (p: Props) => (
  <Svg {...p}>
    <path d="M12 2l7 4v12l-7 4-7-4V6z" />
    <path d="M12 22V12" />
    <path d="M19 6l-7 6-7-6" />
  </Svg>
);

export const IconSun = (p: Props) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </Svg>
);

export const IconMoon = (p: Props) => (
  <Svg {...p}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />
  </Svg>
);

export const IconChart = (p: Props) => (
  <Svg {...p}>
    <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
  </Svg>
);

export const IconTable = (p: Props) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 9.5h18M9 9.5V20" />
  </Svg>
);

export const IconArrowUp = (p: Props) => (
  <Svg {...p} strokeWidth={2.4}>
    <path d="M12 19V6M6 11l6-6 6 6" />
  </Svg>
);

export const IconArrowDown = (p: Props) => (
  <Svg {...p} strokeWidth={2.4}>
    <path d="M12 5v13M6 13l6 6 6-6" />
  </Svg>
);

export const IconWarning = (p: Props) => (
  <Svg {...p}>
    <path d="M10.3 3.9L2.4 17.5A1.9 1.9 0 0 0 4 20.4h16a1.9 1.9 0 0 0 1.7-2.9L13.7 3.9a1.9 1.9 0 0 0-3.4 0z" />
    <path d="M12 9v4M12 17h.01" />
  </Svg>
);

export const IconCritical = (p: Props) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M15 9l-6 6M9 9l6 6" />
  </Svg>
);

export const IconGood = (p: Props) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12.5l2.6 2.6L16 9.6" />
  </Svg>
);

/* ── Sign-in ────────────────────────────────────────────────────────── */

export const IconUser = (p: Props) => (
  <Svg {...p}>
    <circle cx="12" cy="8" r="3.6" />
    <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
  </Svg>
);

export const IconLock = (p: Props) => (
  <Svg {...p}>
    <rect x="4" y="10.5" width="16" height="10" rx="2.2" />
    <path d="M8 10.5V7.6a4 4 0 0 1 8 0v2.9" />
  </Svg>
);

export const IconEye = (p: Props) => (
  <Svg {...p}>
    <path d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12 18 18.2 12 18.2 2.5 12 2.5 12z" />
    <circle cx="12" cy="12" r="2.8" />
  </Svg>
);

export const IconEyeOff = (p: Props) => (
  <Svg {...p}>
    <path d="M9.9 5.9A9.6 9.6 0 0 1 12 5.8c6 0 9.5 6.2 9.5 6.2a17 17 0 0 1-3.3 4.1" />
    <path d="M6.4 7.6A16.8 16.8 0 0 0 2.5 12S6 18.2 12 18.2a9.4 9.4 0 0 0 3.9-.8" />
    <path d="M10.1 10.1a2.8 2.8 0 0 0 3.8 3.8" />
    <path d="M3.5 3.5l17 17" />
  </Svg>
);

export const IconMenu = (p: Props) => (
  <Svg {...p}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Svg>
);

export const IconClose = (p: Props) => (
  <Svg {...p}>
    <path d="M6 6l12 12M18 6L6 18" />
  </Svg>
);

export const IconSignOut = (p: Props) => (
  <Svg {...p}>
    <path d="M14.5 4.5H6.8A1.8 1.8 0 0 0 5 6.3v11.4a1.8 1.8 0 0 0 1.8 1.8h7.7" />
    <path d="M17 15.5L20.5 12 17 8.5M20 12h-9.5" />
  </Svg>
);
